import React, { createContext, useState, useEffect, useContext } from "react";
import axios from "../api/axios";
import { AuthContext } from "./AuthContext";

export const PreferencesContext = createContext();

export const PreferencesProvider = ({ children }) => {
  const [organization, setOrganization] = useState(
    localStorage.getItem("org") || ""
  );
  const [academicYear, setAcademicYear] = useState(
    localStorage.getItem("year") || ""
  );
  const [orgDisplayName, setOrgDisplayName] = useState(
    localStorage.getItem("orgName") || ""
  );
  const [yearDisplayName, setYearDisplayName] = useState(
    localStorage.getItem("yearName") || ""
  );
  const [orgOptions, setOrgOptions] = useState([]);
  const [preferenceMappings, setPreferenceMappings] = useState([]);

  const { user } = useContext(AuthContext);

  const fetchPreferences = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const res = await axios.get("/users/preferences", {
        headers: { "x-auth-token": token },
      });

      const data = res.data;
      if (!Array.isArray(data) || data.length === 0) {
        // No preferences available
        return;
      }
      setPreferenceMappings(data);

      // Use localStorage org if present
      const savedOrg = localStorage.getItem("org");
      const savedYear = localStorage.getItem("year");

      let pref;

      if (savedOrg) {
        pref = data.find((p) => p.organization._id === savedOrg);
      }

      if (!pref) {
        pref = data[0]; //auto-pick
      }

      if (pref?.organization) {
        setOrganization(pref.organization._id);
        setOrgDisplayName(pref.organization.name);
      }
      if (pref?.academicYear) {
        setAcademicYear(pref.academicYear._id);
        setYearDisplayName(pref.academicYear.name);
      } else {
        setAcademicYear("");
        setYearDisplayName("");
      }
    } catch (err) {
      console.error("Fetch preferences failed", err);
    }
  };

  const fetchOrgsIfNeeded = async () => {
    if (user.role !== "super_admin") {
      // No need to fetch orgs — we can use user.organization
      if (user.organization) {
        setOrganization(user.organization._id);
        setOrgDisplayName(user.organization.name);
      }
    } else {
      // Super admin: load all orgs
      const token = localStorage.getItem("token");
      const res = await axios.get("/orgs", {
        headers: { "x-auth-token": token },
      });
      setOrgOptions(res.data);
      if (!organization && res.data.length > 0) {
        const latestOrg = res.data[res.data.length - 1];
        setOrganization(latestOrg._id);
        setOrgDisplayName(latestOrg.name);
      }
    }
  };

  // On mount, fetch from backend (user preferences)
  useEffect(() => {
    if (user) {
      fetchOrgsIfNeeded();
      fetchPreferences();
    }
  }, [user]);

  useEffect(() => {
    localStorage.setItem("org", organization);
    localStorage.setItem("orgName", orgDisplayName);
  }, [organization, orgDisplayName]);

  useEffect(() => {
    localStorage.setItem("year", academicYear);
    localStorage.setItem("yearName", yearDisplayName);
  }, [academicYear, yearDisplayName]);

  const handleOrgChange = (orgId) => {
    const selected = orgOptions.find((o) => o._id === orgId);
    setOrganization(orgId);
    setOrgDisplayName(selected?.name || "");
    setAcademicYear("");
    setYearDisplayName("");

    const mapping = preferenceMappings.find(
      (m) => m.organization._id === orgId
    );

    if (mapping && mapping.academicYear) {
      setAcademicYear(mapping.academicYear._id);
      setYearDisplayName(mapping.academicYear.name);

      // Update localStorage before reload
      localStorage.setItem("org", orgId);
      localStorage.setItem("orgName", selected?.name || "");
      localStorage.setItem("year", mapping.academicYear._id);
      localStorage.setItem("yearName", mapping.academicYear.name);
    } else {
      localStorage.setItem("org", orgId);
      localStorage.setItem("orgName", selected?.name || "");
      localStorage.removeItem("year");
      localStorage.removeItem("yearName");
      console.log("No academic year mapped for this org");
    }

    setTimeout(() => window.location.reload(), 100);
  };

  return (
    <PreferencesContext.Provider
      value={{
        organization,
        setOrganization,
        academicYear,
        setAcademicYear,
        orgDisplayName,
        setOrgDisplayName,
        yearDisplayName,
        setYearDisplayName,
        orgOptions,
        preferenceMappings,
        handleOrgChange,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
};
